import { useCallback, useEffect, useMemo, useState } from "react";

import DailyScheduleService from "./DailyScheduleService";
import { toDateKey, todayDate } from "./data";

/**
 * The days that have a schedule, for the last FILLED_DAYS_YEARS years, in
 * one request. A patient reports at most one routine a day, so five years
 * is ≤ 1830 small rows — cheap enough to hold client-side and share
 * between everything that needs "which days are filled": the Add/Edit
 * calendar (filled / unfilled / selected / future tiles), the Past
 * schedules list (paged locally, ten a page) and the nothing-on-record
 * placeholder gate.
 *
 * `refreshKey` is bumped by the section after every save so a newly
 * captured day shows up without a reload.
 *
 * Returns { days, set, status, reload }:
 *   days   — [{date, entryCount, …}] newest first, or null until loaded
 *   set    — Set of the date keys, for O(1) "is this day filled"
 *   status — "loading" | "idle" | "error"
 */
export const FILLED_DAYS_YEARS = 5;
export const fiveYearsAgo = () => {
  const [y, m, d] = todayDate().split("-").map((x) => parseInt(x, 10));
  return toDateKey(new Date(y - FILLED_DAYS_YEARS, m - 1, d));
};

const useFilledDays = (patientId, refreshKey) => {
  const [days, setDays] = useState(null);
  const [status, setStatus] = useState("loading");
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!patientId) return undefined;
    let stale = false;
    setStatus("loading");
    const svc = new DailyScheduleService();
    svc.days(
      patientId,
      { limit: 2000, since: fiveYearsAgo() },
      (res) => {
        if (stale) return;
        setDays((res && res.items) || []);
        setStatus("idle");
      },
      () => {
        if (!stale) setStatus("error");
      }
    );
    return () => {
      stale = true;
    };
  }, [patientId, refreshKey, tick]);

  const set = useMemo(() => new Set((days || []).map((d) => d.date)), [days]);
  return { days, set, status, reload };
};

export default useFilledDays;
